import { useEffect, useRef, useState } from "react";
import type {
  Appointment,
  AppointmentStatusEvent,
  PatientFileWithGlance,
  ProcedureCode,
  StaffMember,
  VisitSummary,
} from "@dentacore/shared-types";

import {
  changeAppointmentStatus,
  declineAppointment,
  listStatusEvents,
  validateAppointment,
  remindPatient,
  leaveVisit,
} from "../api/appointments";
import { procedureNames, visitMembers } from "./group-visits";
import { ConfirmDialog } from "../ui/confirm-dialog";
import { getPatient, listVisits } from "../api/patients";
import { useWaiting } from "../shell/waiting-context";
import { AppointmentPayment, payForVisits } from "./appointment-payment";
import type { VisitPayment } from "./appointment-payment";
import { useCan } from "../auth/use-can";
import { useModules } from "../shell/modules-context";
import { useAwaitingShown } from "./block-state";
import { formatMoney } from "../patients/money";
import { CompleteVisitDialog } from "./complete-visit-dialog";
import { EditBookingDialog } from "./edit-booking-dialog";
import { cancelLater } from "../api/plans";
import { PlanLink } from "./plan-link";
import { ShiftDialog } from "./shift-dialog";
import { nextStatuses, statusAction, statusLabel } from "./status-graph";
import { visitStep } from "./ring-steps";
import type { PanelRingCommand } from "./ring-steps";
import { WaitlistMatchesStrip } from "./waitlist-matches";
import { blocksTheChair as holdsChair, clockAt, isMovable, localDayOf } from "./grid-geometry";
import { Modal } from "../ui/modal";
import styles from "./appointment-panel.module.css";
import { useBranchCurrency } from "../api/use-branch-currency";
import { useBookingLocation, useBookingScoped } from "./booking-location";
import { LocationBadge } from "./location-badge";
import { FamilyBill } from "./family-bill";

/**
 * The selected booking, in detail — the mockup's *Selected appointment* card.
 *
 * ── What it draws, and where each piece comes from ──────────────────────────
 *
 * The mockup's card is a micro-label, the patient's name in the display serif,
 * a meta line of `age · file · time`, the procedure and its note, a key/value
 * table, and then one of two treatments depending on whether the patient has
 * been here before. All of that is assembled here from three sources: the
 * appointment the calendar already has, the patient's file, and their visits.
 *
 * ── Nothing new was needed on the server ────────────────────────────────────
 *
 * **First-visit-versus-returning is derived, not stored.** A visit is a date
 * with something on it (`VisitsService`), so "has this patient been here
 * before" is *is there a visit dated earlier than this appointment* — which
 * `listVisits` already answers. Adding an `is_new` column would have been a
 * second source of truth for something the records already say, and it would
 * start lying the moment a visit was back-dated or deleted.
 *
 * Critical flags need nothing either: they ride on the appointment payload
 * already, because STORY-019 put them there precisely so they surface where the
 * clinician is looking rather than only on the patient's own page (§6.5).
 *
 * ── What the mockup draws that this does not ────────────────────────────────
 *
 * **The first-visit intake table and its *Send intake form* button** — *Intake
 * form · Reported · Referral · Insurance*. Three of those four have no column
 * in `patients` and no feature anywhere behind them, so drawing them would be
 * four rows of em-dashes reading as broken data rather than as an absent
 * feature. Confirmed with the client on 2026-08-23 (P3 Q13): build the
 * treatment that has data behind it, and let intake capture be chosen
 * deliberately as its own story.
 *
 * **Cancel appointment** is STORY-037's — cancelling is a status transition,
 * and the transition rules, their audit trail and their outbox events all
 * arrive together there rather than one button early.
 */
export function AppointmentPanel({
  appointment,
  chairName,
  staff,
  timezone,
  onReschedule,
  onOpenAppointment,
  onOpenFile,
  onStatusChanged,
  onClose,
  codes,
  location,
  ringCommand = null,
  onRingTaken,
}: {
  appointment: Appointment;
  /**
   * An action asked for from the quick ring (STORY-221, QR8), carried out by
   * pressing this panel's own button for it — so the chain question, the
   * Complete visit dialog and the location scoping are exactly the button's.
   */
  ringCommand?: PanelRingCommand | null;
  /** The ring's request has been taken, so it is not carried out twice. */
  onRingTaken?: () => void;
  chairName: string;
  /**
   * **The booking's location, when several are shown** (CF-D15, CF-D17): its
   * badge and name above the patient, and a note that every action here goes
   * to it. Absent on a single-location calendar, which draws as it always did.
   */
  location?: { name: string; code: string; hue: number };
  /**
   * This branch's catalogue, for the completion dialog's tooth question.
   * Passed down rather than fetched here: the calendar already holds it for
   * the booking form, and two readers of one list is two requests.
   */
  codes: readonly ProcedureCode[] | null;
  /** The clinic's staff, for resolving this booking's own provider by id. */
  staff: readonly StaffMember[];
  /** The branch's own zone — the time the patient will actually arrive at. */
  timezone: string;
  onReschedule: () => void;
  onOpenFile: (patientId: string) => void;
  /** Open another visit of this appointment's plan on the calendar (CH7). */
  onOpenAppointment?: (appointmentId: string, startsAt: string) => void;
  /** A status change rewrites the block on the grid, so the day is re-read. */
  onStatusChanged: () => void;
  onClose: () => void;
}): React.JSX.Element {
  const here = useBookingScoped();
  const bookingLocation = useBookingLocation();
  // Reminders are Chatbot's (MOD-D16), so the button needs it and the right the
  // route asks for — hidden otherwise rather than refused on click (QF-D9).
  const modules = useModules();
  const can = useCan();
  /** Chatbot on and allowed to validate — or the booking is an ordinary one (QF-D9, QF-D21). */
  const awaitingShown = useAwaitingShown();
  const canRemind = modules.has("CHATBOT") && can("reminders.send");
  const currency = useBranchCurrency();
  const [file, setFile] = useState<PatientFileWithGlance | null>(null);
  const [visits, setVisits] = useState<VisitSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  /** The bill for this visit, one patient or a family (FV-D14). */
  const [billing, setBilling] = useState(false);
  /** The family member whose *Take out* is waiting on its confirm (FV-D10). */
  const [leaving, setLeaving] = useState<Appointment | null>(null);

  const patientId = appointment.patient?.id ?? null;

  useEffect(() => {
    if (patientId === null) {
      // A buffer block has no patient, so there is nothing to read. Settled
      // rather than left null, or the panel waits for a request never sent.
      setFile(null);
      setVisits([]);
      return;
    }

    const controller = new AbortController();
    setFile(null);
    setVisits(null);
    setError(null);

    Promise.all([
      here(getPatient)(patientId, controller.signal),
      here(listVisits)(patientId, controller.signal),
    ])
      .then(([patientFile, list]) => {
        setFile(patientFile);
        setVisits(list.visits);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        // Said out loud. The panel's whole job is showing what is known about
        // this patient, and a failed read that renders as "no history" is worse
        // than one that renders as a fault.
        setVisits([]);
        setError(cause instanceof Error ? cause.message : "Could not read this patient's file.");
      });

    return () => controller.abort();
  }, [patientId]);

  const [busy, setBusy] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  /**
   * Completing is the one move that asks a question first — which tooth was
   * worked on — so it opens a dialog instead of firing the request.
   */
  const [completing, setCompleting] = useState(false);

  /**
   * Declining is two clicks, not one (ADR-0005, STORY-056).
   *
   * The first opens a reason box, the second cancels. **Both halves earn their
   * place.** A decline is a cancellation of a booking the patient believes they
   * have, sitting one button away from Validate, and a misclick there is the
   * expensive kind. And ADR-0005 calls a decline *"a cancellation with a
   * reason"* — the reason stays optional, as `ChangeStatusDto.note` settled for
   * every other cancellation, but the box has to exist for anyone to write one.
   */
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState("");

  /**
   * **Three states, not a boolean.** "Sent" has to survive on screen after the
   * request finishes, because the only thing stopping a second reminder going
   * to the same patient is the person reading this line.
   */
  const [reminder, setReminder] = useState<"idle" | "sending" | "sent">("idle");
  const [reminderError, setReminderError] = useState<string | null>(null);

  const { refresh: refreshWaiting } = useWaiting();

  // Selecting a different block puts the panel back to its resting state. Left
  // out, a half-typed reason would follow the reader onto the next booking.
  useEffect(() => {
    setDeclining(false);
    setReason("");
    // Otherwise "Reminder sent" follows the reader onto the next booking and
    // reads as a message that was never sent.
    setReminder("idle");
    setReminderError(null);
  }, [appointment.id]);

  /**
   * The recorded transitions, opened on demand (QA #39).
   *
   * **Not loaded with the panel.** Most of the time the current status is the
   * whole question, and a request per block selected would be a request per
   * click on the grid. It is fetched when somebody asks who changed this, which
   * is the moment the answer matters.
   */
  const [history, setHistory] = useState<AppointmentStatusEvent[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showEarlier, setShowEarlier] = useState(false);

  // Collapsed and dropped whenever the panel changes booking, so the trail
  // shown can never belong to the appointment that was selected before.
  useEffect(() => {
    setShowHistory(false);
    setShowEarlier(false);
    setHistory(null);
    setHistoryError(null);
  }, [appointment.id]);

  useEffect(() => {
    if (!showHistory) return;

    const controller = new AbortController();
    here(listStatusEvents)(appointment.id, controller.signal)
      .then((events) => {
        setHistory(events);
        setHistoryError(null);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        // **Said, not swallowed into an empty list.** "Nothing was recorded"
        // and "the record could not be read" are different facts, and this is
        // an audit trail — reporting a failed read as "no changes" is the one
        // wrong answer it must never give.
        setHistoryError(cause instanceof Error ? cause.message : "Could not read the history.");
      });

    return () => controller.abort();
  }, [showHistory, appointment.id]);

  /**
   * The moves available from where this booking is now.
   *
   * A dead end yields none, and the row of buttons disappears rather than
   * rendering disabled ones — a completed visit is finished, not a visit whose
   * buttons happen to be greyed out.
   */
  /**
   * **Only the moves this person may make** (QA third run): a status change is
   * `appointments.status`, and cancelling is `appointments.cancel` on top —
   * the server's rule, so no button opens a refusal.
   */
  const moves = can("appointments.status")
    ? nextStatuses(appointment.status).filter(
        (next) => next !== "CANCELLED" || can("appointments.cancel"),
      )
    : [];

  /**
   * An open "cancel the rest of the plan?" question (CH11, CH13): the status
   * being moved to, while the person decides.
   */
  const [cancelAsk, setCancelAsk] = useState<string | null>(null);

  const changeStatus = (
    to: string,
    wholeMouth: { appointmentId: string; codeIds: string[] }[] = [],
    alsoCancelLater = false,
    payment: VisitPayment | null = null,
  ): Promise<boolean> => {
    setBusy(true);
    setStatusError(null);

    const own = wholeMouth.find((row) => row.appointmentId === appointment.id)?.codeIds ?? [];
    return here(changeAppointmentStatus)(appointment.id, {
      status: to,
      ...(own.length === 0 ? {} : { wholeMouthCodeIds: own }),
      // A family visit moves together, each child billed their own (FV-D4, FV-D5).
      ...(wholeMouth.length > 1 ? { wholeMouthByAppointment: wholeMouth } : {}),
    })
      .then(async () => {
        // **This visit first, then the later ones** — if this one is refused
        // nothing else is touched. A later visit the rules refuse is said, not
        // swallowed (CH10).
        /**
         * **The payment chosen as the visit closed** (FX30), against the
         * invoice the completion just raised — before the panel re-reads, so
         * its Payment section opens already showing it. A payment that does
         * not land leaves the visit complete and says why.
         */
        if (payment !== null) {
          const failed = await payForVisits(
            visitMembers(appointment),
            payment,
            (cents) => formatMoney(cents, currency),
            bookingLocation?.branchId ?? null,
          );
          if (failed !== null) setStatusError(failed);
        }

        const chain = appointment.chain ?? null;
        if (alsoCancelLater && chain !== null) {
          const result = await here(cancelLater)(chain.planId, appointment.id);
          if (result.refused.length > 0) {
            setStatusError(
              `Done, but ${String(result.refused.length)} later ${
                result.refused.length === 1 ? "visit" : "visits"
              } could not be cancelled: ${result.refused.map((row) => row.message).join(" ")}`,
            );
          }
        }
        onStatusChanged();
        return true;
      })
      .catch((cause: unknown) => {
        // The server's own sentence, which names what *is* allowed from here.
        setStatusError(cause instanceof Error ? cause.message : "That status change did not save.");
        return false;
      })
      .finally(() => setBusy(false));
  };

  /**
   * Pressing one of the status buttons below — the quick ring presses the
   * same one (STORY-221), so it asks exactly what the button asks.
   */
  const pressMove = (next: string): void => {
    // **Completing charts before it completes.** Every other move
    // is a status change and nothing else; this one writes to the
    // clinical record and raises an invoice, and the tooth it
    // needs is not on the appointment.
    if (next === "COMPLETED") setCompleting(true);
    // **A chained visit asks about the rest first** (CH11, CH13) —
    // before anything is sent, so closing the question changes
    // nothing at all.
    else if (
      (next === "CANCELLED" || next === "NO_SHOW") &&
      (appointment.chain?.laterBooked ?? 0) > 0
    ) {
      setCancelAsk(next);
    } else void changeStatus(next);
  };

  /** The *Remind* button below, which the quick ring presses too. */
  const sendReminder = (): void => {
    setReminder("sending");
    setReminderError(null);
    here(remindPatient)(appointment.id)
      .then(() => setReminder("sent"))
      .catch((cause: unknown) => {
        setReminder("idle");
        setReminderError(cause instanceof Error ? cause.message : "That reminder did not send.");
      });
  };

  /** Bumped by the ring's *Take payment*: opens the payment form on what is owed. */
  const [payAsked, setPayAsked] = useState<number | undefined>(undefined);

  /**
   * **The quick ring's request, carried out by this panel's own buttons**
   * (STORY-221, QR8). Each action is the button the reader would have pressed,
   * so nothing here can do what the panel would not: a step not on offer
   * (`moves`), a reminder without Chatbot, or a payment on an unfinished
   * visit does nothing. Confirming and checking in are the panel's two
   * clicks, the second only once the first has saved.
   */
  const runRing = (key: PanelRingCommand["key"]): void => {
    if (key === "step") {
      const step = visitStep(appointment.status);
      const [first, second] = step?.to ?? [];
      if (first === undefined || !moves.includes(first)) return;
      if (second === undefined) pressMove(first);
      else
        void changeStatus(first).then((saved) => {
          if (saved) void changeStatus(second);
        });
    } else if (key === "cancel") {
      if (moves.includes("CANCELLED")) pressMove("CANCELLED");
    } else if (key === "remind") {
      if (appointment.patient !== null && canRemind && reminder === "idle") sendReminder();
    } else {
      setPayAsked((count) => (count ?? 0) + 1);
    }
  };
  const latestRunRing = useRef(runRing);
  latestRunRing.current = runRing;
  /** The last request carried out — each runs once, however often this re-renders. */
  const ringTaken = useRef<number | null>(null);
  useEffect(() => {
    if (ringCommand === null || ringCommand.id !== appointment.id) return;
    if (ringTaken.current === ringCommand.nonce) return;
    ringTaken.current = ringCommand.nonce;
    onRingTaken?.();
    latestRunRing.current(ringCommand.key);
  }, [ringCommand, appointment.id, onRingTaken]);

  /**
   * The staff half of ADR-0005, in one function because the two acts differ
   * only in which request they send.
   *
   * **Both refresh the calendar.** Validating repaints one block — the stripes
   * and the ring come off, and the day stops asking to be looked at. Declining
   * removes it entirely, because a cancelled booking draws nothing (T2). The
   * panel does not guess at either: it re-reads.
   */
  const decide = (act: () => Promise<unknown>, failure: string): void => {
    setBusy(true);
    setStatusError(null);

    act()
      .then(() => {
        // The header chip counts this booking, so it has to stop counting it
        // the moment the flag comes off (STORY-057, HC2). This is the whole of
        // what "live" means without a transport: the person reading the number
        // is the person who just acted.
        refreshWaiting();
        onStatusChanged();
      })
      .catch((cause: unknown) => {
        // `NOT_AWAITING_VALIDATION` reads as a sentence here: somebody else got
        // to it first, and the answer is to reload rather than to try again.
        setStatusError(cause instanceof Error ? cause.message : failure);
      })
      .finally(() => setBusy(false));
  };

  const flags = appointment.patient?.flags ?? [];
  const critical = flags.filter((flag) => flag.severity === "CRITICAL");
  const provider = staff.find((person) => person.id === appointment.providerId) ?? null;

  const day = localDayOf(appointment.startsAt, timezone);
  const earlier = visits?.filter((visit) => visit.date < day) ?? [];
  const returning = earlier.length > 0;

  return (
    <aside className={styles.panel} data-testid="appointment-panel">
      <div className={styles.head}>
        <span className={styles.label}>Selected appointment</span>
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Clear the selection"
          data-testid="panel-close"
        >
          ×
        </button>
      </div>

      {location !== undefined && (
        <div className={styles.location} data-testid="panel-location">
          <LocationBadge code={location.code} hue={location.hue} name={location.name} />
          {location.name}
        </div>
      )}

      <h2 className={styles.name} data-testid="panel-name">
        {appointment.patient === null
          ? "Chair held"
          : `${appointment.patient.firstName} ${appointment.patient.lastName}`}
      </h2>

      {/*
        **Facts as chips, not a dot-joined sentence** — the same change the visit
        dialog got on 2026-09-07. `36 · #00001 · 07:00–07:45` is an age, a file
        number and a time span reading as one string, and the middle dot is
        doing the work of three labels.

        The clock keeps the accent, because it is the one a reader is usually
        looking for.
      */}
      <p className={styles.meta} data-testid="panel-meta">
        {metaOf(appointment, file, timezone).map((fact) => (
          <span
            key={fact.text}
            className={fact.strong === true ? styles.factStrong : styles.fact}
            title={fact.title}
          >
            {fact.text}
          </span>
        ))}
      </p>

      <p className={styles.procedure} data-testid="panel-procedure">
        {/*
          **The catalogue label is the fallback, not an em dash.**

          `procedureSummary` is what somebody typed, and picking a procedure from the catalogue
          leaves it empty — so a booking made by picking, and every booking made from a treatment
          plan, showed "—" while the tray below it listed that same procedure's materials.
        */}
        {procedureNames(appointment) ?? (appointment.patient === null ? "Chair held" : "—")}
      </p>

      {/*
        **Everyone in a family visit** (FV-D15, FV-D10): each with what they come
        for, their own file, and *Take out of this visit*, which cancels just
        them and closes the gap behind them. The rest of the panel acts on the
        whole visit, which moves together (FV-D4).
      */}
      {(appointment.party ?? []).length > 0 && (
        <div className={styles.party} data-testid="panel-party">
          <span className={styles.partyLabel}>
            Family visit · {visitMembers(appointment).length} patients, one after another
          </span>
          {visitMembers(appointment).map((member) => (
            <div key={member.id} className={styles.partyRow} data-testid="panel-party-row">
              <span className={styles.partyName}>
                {member.patient === null
                  ? "—"
                  : `${member.patient.firstName} ${member.patient.lastName}`}
                <span className={styles.partyWhat}>
                  {procedureNames(member) ?? "Not decided yet"} ·{" "}
                  {clockAt(member.startsAt, timezone)}
                </span>
              </span>
              <button
                type="button"
                className={styles.partyOpen}
                onClick={() => onOpenFile(member.patient?.id ?? "")}
              >
                Open file
              </button>
              {can("appointments.cancel") && isMovable(member) && (
                <button
                  type="button"
                  className={styles.partyOpen}
                  disabled={busy}
                  onClick={() => setLeaving(member)}
                  data-testid="panel-party-leave"
                >
                  Take out
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {leaving !== null && (
        <ConfirmDialog
          title={`Take ${leaving.patient?.firstName ?? "them"} out of this visit?`}
          confirmLabel="Take out"
          tone="danger"
          busy={busy}
          onCancel={() => setLeaving(null)}
          onConfirm={() => {
            const member = leaving;
            setLeaving(null);
            setBusy(true);
            setStatusError(null);
            here(leaveVisit)(member.id)
              .then(() => onStatusChanged())
              .catch((cause: unknown) =>
                setStatusError(cause instanceof Error ? cause.message : "That did not save."),
              )
              .finally(() => setBusy(false));
          }}
          testId="panel-party-leave-confirm"
        >
          <p>
            Their booking is cancelled, and everyone after them moves up so the family stays back to
            back.
          </p>
        </ConfirmDialog>
      )}
      {/* The bill for any visit with a patient — one person or a family (FV-D14). */}
      {appointment.patient !== null && (
        <button
          type="button"
          className={styles.partyBill}
          onClick={() => setBilling(true)}
          data-testid="panel-family-bill"
        >
          See the bill for this visit
        </button>
      )}
      {billing && (
        <FamilyBill
          appointment={appointment}
          currency={currency}
          where={location?.name ?? null}
          timezone={timezone}
          onClose={() => setBilling(false)}
        />
      )}

      {/*
        **Critical flags, above the fold and before anything else clinical.**
        Working plan §6.5 puts them on this card by name, and STORY-019 already
        carries them on the payload so they cannot be forgotten here. Severity
        decides the treatment; a paler tint alone would not survive STORY-023's
        colour-vision constraint, so the critical band carries a rule and a
        word as well as a colour.
      */}
      {critical.length > 0 && (
        <div className={styles.critical} data-testid="panel-critical">
          <span className={styles.criticalLabel}>Critical</span>
          <span className={styles.criticalText}>
            {critical.map((flag) => flag.label).join(" · ")}
          </span>
        </div>
      )}

      {/*
        **The staff step, and the only place it exists** (ADR-0005, STORY-056).

        The wording is doing real work. Staff read "awaiting validation" as
        "waiting for me to approve it" unless told otherwise, and the whole
        point of ADR-0005 is that the decision was already made — by the
        patient, in the conversation. So the sentence says the chair is held
        already, which is what stops somebody treating a busy morning as a
        reason to leave the queue alone and the slot notionally free.

        Below the critical band by design: an allergy outranks admin.
      */}
      {appointment.awaitingValidation && awaitingShown && (
        <div className={styles.awaiting} data-testid="panel-awaiting">
          <span className={styles.awaitingLabel}>Awaiting validation</span>
          <span className={styles.awaitingText}>
            Booked over WhatsApp when the patient confirmed this time. It holds the chair already —
            validating just records that somebody here has seen it.
          </span>

          {declining ? (
            <>
              <input
                type="text"
                className={styles.reason}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Why, in a few words"
                aria-label="Why the booking is being declined"
                maxLength={500}
                data-testid="decline-reason"
              />
              <div className={styles.awaitingActions}>
                <button
                  type="button"
                  className={styles.destructive}
                  disabled={busy}
                  onClick={() =>
                    decide(
                      () => here(declineAppointment)(appointment.id, reason),
                      "That decline did not save.",
                    )
                  }
                  data-testid="panel-decline-confirm"
                >
                  Cancel the booking
                </button>
                <button
                  type="button"
                  className={styles.move}
                  disabled={busy}
                  onClick={() => setDeclining(false)}
                >
                  Keep it
                </button>
              </div>
            </>
          ) : (
            <div className={styles.awaitingActions}>
              <button
                type="button"
                className={styles.move}
                disabled={busy}
                onClick={() =>
                  decide(
                    () => here(validateAppointment)(appointment.id),
                    "That validation did not save.",
                  )
                }
                data-testid="panel-validate"
              >
                Validate
              </button>
              <button
                type="button"
                className={styles.destructive}
                disabled={busy}
                onClick={() => setDeclining(true)}
                data-testid="panel-decline"
              >
                Decline
              </button>
            </div>
          )}
        </div>
      )}

      {/* The tray list is deliberately not shown here — Anthony, 2026-10-11. */}

      {/*
        **The plan this visit belongs to** (decision D6) — read it, attach one,
        or say it is not this visit. Draws nothing for a held chair, and nothing
        for a patient with no plan waiting, which is most bookings (TP4).
      */}
      <PlanLink
        appointmentId={appointment.id}
        patientId={appointment.patient?.id ?? null}
        timezone={timezone}
        {...(onOpenAppointment === undefined ? {} : { onOpenAppointment })}
      />

      {/*
        **Remind this patient, now** — no scheduler behind it, and nothing
        sends on its own. The button is drawn only for a real booking: a buffer
        block holds a chair and has nobody to message.

        Failures are said out loud rather than swallowed, because every one of
        them is actionable by the person standing here — a branch with no
        location set, a booking with no procedure, a patient with no number.
      */}
      {appointment.patient !== null && canRemind && (
        <div className={styles.remindRow}>
          <button
            type="button"
            className={styles.remind}
            disabled={reminder !== "idle"}
            onClick={sendReminder}
            data-testid="panel-remind"
          >
            {reminder === "sending" ? "Sending…" : "Remind"}
          </button>

          {reminder === "sent" && (
            <span className={styles.remindNote} data-testid="panel-remind-sent">
              Reminder sent on WhatsApp.
            </span>
          )}
        </div>
      )}

      {reminderError !== null && (
        <p className={styles.error} data-testid="panel-remind-error">
          {reminderError}
        </p>
      )}

      <dl className={styles.rows}>
        <Row label="Chair" value={chairName} testId="panel-chair" />
        <Row
          label="Provider"
          value={provider === null ? "Not assigned" : `${provider.firstName} ${provider.lastName}`}
          testId="panel-provider"
        />
        <Row label="Booked" value={bookedOf(appointment)} testId="panel-booked" />
        <Row label="Status" value={statusLabel(appointment.status)} testId="panel-status" />
        {flags.length > critical.length && (
          <Row
            label="Flags"
            value={flags
              .filter((flag) => flag.severity !== "CRITICAL")
              .map((flag) => flag.label)
              .join(" · ")}
            testId="panel-flags"
          />
        )}
      </dl>

      {error !== null && (
        <p className={styles.error} data-testid="panel-error">
          {error}
        </p>
      )}

      {appointment.patient !== null && visits === null && (
        <p className={styles.reading}>Reading this patient&rsquo;s file…</p>
      )}

      {appointment.patient !== null && visits !== null && error === null && (
        <>
          {returning ? (
            /*
              **Previous visits are a button, not a list** (Anthony, 2026-10-11).
              Four visits of history made the panel taller than the screen; the
              count is what a clinician needs at a glance, and the list is one
              click away in a popup.
            */
            <div className={styles.history} data-testid="panel-history">
              <button
                type="button"
                className={styles.earlierButton}
                onClick={() => setShowEarlier(true)}
                data-testid="panel-earlier-open"
              >
                <span>Previous procedures</span>
                <span className={styles.count} data-testid="panel-visit-count">
                  {earlier.length === 1
                    ? "1 previous visit"
                    : `${String(earlier.length)} previous visits`}
                </span>
              </button>
            </div>
          ) : (
            /*
              **The first-visit treatment, without the intake table** (P3 Q13).
              The guidance is the mockup's own sentence and is the clinically
              useful half: it tells the clinician to expect a longer chair.
            */
            <div className={styles.first} data-testid="panel-first-visit">
              <span className={styles.firstLabel}>First visit</span>
              <p className={styles.firstNote}>
                Nothing charted yet — allow time for a full examination, radiographs and charting
                before treating.
              </p>
            </div>
          )}
        </>
      )}

      {/*
        **The status machine, as the moves that are actually available.**

        The mockup's control is a button that cycles a list, which is why the
        graph had to be decided rather than read (P3 Q3). Offering only the
        legal next steps is better than cycling: a receptionist sees that a
        checked-in patient can start treatment, be marked absent or be
        cancelled, and nothing else — which is the rule, made visible.

        The server refuses anything illegal regardless; this is affordance, not
        enforcement.
      */}
      {/*
        **The money for a finished visit** (STORY-187). Only drawn for a completed
        appointment and only for someone who may record payments; the Billing tab
        remains the place for everything else.
      */}
      {/* A payment recorded here clears a "nothing was recorded" left by closing (FQ-06). */}
      <AppointmentPayment
        appointment={appointment}
        onRecorded={() => setStatusError(null)}
        openFirst={payAsked}
      />

      {moves.length > 0 && (
        <div className={styles.moves} data-testid="panel-status-moves">
          {moves.map((next) => (
            <button
              key={next}
              type="button"
              className={next === "CANCELLED" ? styles.destructive : styles.move}
              disabled={busy}
              onClick={() => pressMove(next)}
              data-testid="panel-status-move"
              data-to={next}
            >
              {statusAction(next, appointment.status)}
            </button>
          ))}
        </div>
      )}

      {statusError !== null && (
        <p className={styles.error} data-testid="panel-status-error">
          {statusError}
        </p>
      )}

      {cancelAsk !== null && (
        <ShiftDialog
          kind="cancel"
          later={appointment.chain?.laterBooked ?? 0}
          onAnswer={(answer) => {
            const to = cancelAsk;
            setCancelAsk(null);
            if (answer === null) return;
            void changeStatus(to, [], answer);
          }}
        />
      )}

      {completing && (
        <CompleteVisitDialog
          appointment={appointment}
          codes={codes}
          busy={busy}
          onCancel={() => setCompleting(false)}
          onComplete={(wholeMouth, payment) => {
            setCompleting(false);
            // The charting has already been written against this appointment;
            // the status move is what triggers the invoice for it. Whole-mouth
            // work is the exception — it cannot be charted against a tooth, so
            // it rides on this request and is billed from the catalogue. The
            // payment, if any, is recorded on that invoice once it exists.
            void changeStatus("COMPLETED", wholeMouth, false, payment);
          }}
        />
      )}

      {/*
        **Who moved this, and when** (QA #39, working plan §6.2).

        P3 Q4 lets any signed-in user change a status *because* every change is
        attributed, so this trail is the control that makes the permissive rule
        safe. It had been written faithfully since STORY-037 and shown nowhere.

        Behind a disclosure rather than always open: the panel's job is the
        booking in front of you, and four lines of audit above the waitlist
        strip would push the thing people came for off the screen.
      */}
      <button
        type="button"
        className={styles.statusHistoryToggle}
        onClick={() => setShowHistory((open) => !open)}
        aria-expanded={showHistory}
        data-testid="panel-status-history-toggle"
      >
        {showHistory ? "Hide history" : "Status history"}
      </button>

      {showHistory && (
        <ol className={styles.statusHistory} data-testid="panel-status-history">
          {historyError !== null ? (
            <li className={styles.statusHistoryEmpty} data-testid="panel-status-history-error">
              {historyError}
            </li>
          ) : history === null ? (
            <li className={styles.statusHistoryEmpty}>Reading…</li>
          ) : history.length === 0 ? (
            <li className={styles.statusHistoryEmpty} data-testid="panel-status-history-empty">
              Nothing recorded — this booking has not changed status since it was made.
            </li>
          ) : (
            history.map((event) => (
              <li
                key={event.id}
                className={styles.statusHistoryRow}
                data-testid="panel-status-history-row"
              >
                <span className={styles.statusHistoryMove}>
                  {event.fromStatus === null
                    ? statusLabel(event.toStatus)
                    : `${statusLabel(event.fromStatus)} → ${statusLabel(event.toStatus)}`}
                </span>
                <span className={styles.statusHistoryWho}>
                  {/* Null once somebody has been removed — the act still happened. */}
                  {event.changedByName ?? "a since-removed account"} ·{" "}
                  {new Date(event.changedAt).toLocaleString(undefined, {
                    dateStyle: "medium",
                    timeStyle: "short",
                    timeZone: timezone,
                  })}
                </span>
                {event.note !== null && (
                  <span className={styles.statusHistoryNote}>{event.note}</span>
                )}
              </li>
            ))
          )}
        </ol>
      )}

      {/*
        **The slot came free, so who is waiting for one?** (STORY-038.)

        Only for a booking that has actually stopped holding its chair —
        cancelled or no-show, the two states §6.1 frees immediately. Offering a
        queue for a live booking is how a screen double-books somebody.
      */}
      {!holdsChair(appointment) && (
        <WaitlistMatchesStrip
          appointment={appointment}
          timezone={timezone}
          onBooked={onStatusChanged}
        />
      )}

      <div className={styles.actions}>
        {appointment.patient !== null && (
          <button
            type="button"
            className={styles.secondary}
            onClick={() => onOpenFile(appointment.patient?.id ?? "")}
            data-testid="panel-open-file"
          >
            Open file
          </button>
        )}
        {/* QA4-D3: what it is for, its teeth, who treats — not the patient or the slot. */}
        {can("appointments.book") && isMovable(appointment) && (
          <button
            type="button"
            className={styles.secondary}
            onClick={() => setEditing(true)}
            data-testid="open-edit-booking"
          >
            Edit booking
          </button>
        )}
        {can("appointments.move") && (
          <button
            type="button"
            className={styles.secondary}
            // §6.1: reschedule is disabled for completed and cancelled bookings.
            // The server refuses them too (`NOT_MOVABLE`) — this only stops the
            // dialog opening on a booking whose every slot would bounce.
            disabled={!isMovable(appointment)}
            onClick={onReschedule}
            data-testid="open-reschedule"
          >
            Reschedule
          </button>
        )}
      </div>

      {location !== undefined && (
        <p className={styles.locationNote} data-testid="panel-location-note">
          These act on {location.name}, whichever locations are shown.
        </p>
      )}

      {editing && (
        <EditBookingDialog
          appointment={appointment}
          codes={codes ?? []}
          staff={staff}
          onSaved={onStatusChanged}
          onClose={() => setEditing(false)}
        />
      )}

      {showEarlier && (
        <Modal
          label="Previous procedures"
          testId="panel-earlier-dialog"
          onClose={() => setShowEarlier(false)}
        >
          <h2 className={styles.earlierTitle}>Previous procedures</h2>
          <div className={styles.earlierList}>
            {earlier.map((visit) => (
              <div key={visit.date} className={styles.visit} data-testid="panel-visit">
                <span className={styles.visitDate}>{visit.date}</span>
                <span className={styles.visitWhat}>
                  <span className={styles.visitTitle}>{visit.title ?? "Visit"}</span>
                  <span className={styles.visitBy}>{visit.providerName ?? visit.branchName}</span>
                </span>
              </div>
            ))}
          </div>
          <div className={styles.earlierActions}>
            <button
              type="button"
              className={styles.secondary}
              onClick={() => setShowEarlier(false)}
              data-testid="panel-earlier-close"
            >
              Close
            </button>
          </div>
        </Modal>
      )}
    </aside>
  );
}

function Row({
  label,
  value,
  testId,
}: {
  label: string;
  value: string;
  testId: string;
}): React.JSX.Element {
  return (
    <div className={styles.row}>
      <dt className={styles.rowKey}>{label}</dt>
      <dd className={styles.rowValue} data-testid={testId}>
        {value}
      </dd>
    </div>
  );
}

/**
 * `39 · #00428 · 11:30–12:15`, the mockup's meta line.
 *
 * **Age, file number and time, and each is dropped rather than faked when it is
 * not known.** A buffer block has no patient and so no file number; a patient
 * with no recorded date of birth has no age, which §6.5 allows for explicitly
 * because the WhatsApp agent creates files from a name and a number alone.
 */
interface Fact {
  text: string;
  /** What the chip means, for anything a bare number does not say. */
  title?: string;
  /** The clock, which reads first. */
  strong?: boolean;
}

function metaOf(
  appointment: Appointment,
  file: PatientFileWithGlance | null,
  timezone: string,
): Fact[] {
  const facts: Fact[] = [];

  // **The server derives the age**, on every read, from the date of birth
  // (§6.5 — a stored age is wrong the day after a birthday). Recomputing it
  // here would be a second implementation of a rule that already has one.
  //
  // Said as "36 years" rather than as a bare "36": on its own in a chip beside
  // a file number it was a digit with no unit, which is the same complaint the
  // client made about the tooth numbers on the history tab.
  if (file?.age != null) facts.push({ text: `${String(file.age)} years`, title: "Age" });
  if (appointment.patient !== null) {
    facts.push({ text: appointment.patient.fileNumberDisplay, title: "File number" });
  }

  facts.push({
    text: `${clockAt(appointment.startsAt, timezone)}–${clockAt(appointment.endsAt, timezone)}`,
    title: "Booked for",
    strong: true,
  });

  return facts;
}

/**
 * `45 min + 15 turnover`, the mockup's wording.
 *
 * The turnover half is omitted at zero rather than written as `+ 0`, which is
 * the buffer-block case — a block holds a chair and cleans nothing.
 */
function bookedOf(appointment: Appointment): string {
  const minutes = Math.round(
    (new Date(appointment.endsAt).getTime() - new Date(appointment.startsAt).getTime()) / 60_000,
  );

  return appointment.turnoverMinutes === 0
    ? `${String(minutes)} min`
    : `${String(minutes)} min + ${String(appointment.turnoverMinutes)} turnover`;
}

/**
 * Which local day an instant falls on in the branch's zone, as `YYYY-MM-DD`.
 *
 * The same frame `VisitsService` resolves a visit's date in (P2 Q8), which is
 * what makes comparing the two meaningful — a visit dated in the branch's zone
 * and an appointment dated in the reader's would disagree either side of
 * midnight.
 */
